// Build 1 (session 60) — bare file-view modal for the expandable section layout.
// Shows file name + type + size + date + a Download button. No preview render,
// no AI analysis, no edit. Renders null-safe for legacy documents whose
// fileSize / mimeType were never captured (shows "—").

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon } from "lucide-react";

export interface FileViewModalFile {
  id: string;
  filename: string;
  mimeType?: string | null;
  fileSize?: number | null;
  uploadedAt?: string | null;
}

function iconForFilename(filename: string) {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  if (["png", "jpg", "jpeg", "gif", "webp", "heic", "bmp"].includes(ext)) return ImageIcon;
  if (["xls", "xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (["pdf", "doc", "docx", "txt", "rtf"].includes(ext)) return FileText;
  return FileIcon;
}

function formatBytes(bytes: number | null | undefined): string | null {
  if (bytes == null || isNaN(bytes)) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  // Date + time so two uploads on the same day are distinguishable.
  return d.toLocaleString("en-CA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function typeLabel(file: FileViewModalFile): string {
  if (file.mimeType) return file.mimeType;
  const ext = file.filename.split(".").pop()?.toUpperCase();
  return ext ? `${ext} file` : "—";
}

interface FileViewModalProps {
  file: FileViewModalFile | null;
  onClose: () => void;
  onDownload: (docId: string) => void;
}

function MetaRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right break-all">{value ?? "—"}</span>
    </div>
  );
}

export function FileViewModal({ file, onClose, onDownload }: FileViewModalProps) {
  const open = !!file;
  const Icon = file ? iconForFilename(file.filename) : FileIcon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Attachment</DialogTitle>
        </DialogHeader>
        {file && (
          <div className="flex flex-col gap-4 py-2">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-muted">
                <Icon className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-center break-all">{file.filename}</p>
            </div>
            <div className="space-y-1.5 rounded-md border border-border p-3">
              <MetaRow label="Type" value={typeLabel(file)} />
              <MetaRow label="Size" value={formatBytes(file.fileSize)} />
              <MetaRow label="Added" value={formatDate(file.uploadedAt)} />
            </div>
            <Button
              className="w-full"
              onClick={() => onDownload(file.id)}
              aria-label={`Download ${file.filename}`}
            >
              <Download className="h-4 w-4 mr-2" />
              Download original
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
